import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import DotLegalAutocomplete, { AutocompleteOption } from "../../common/autocomplete/DotLegalAutocomplete";
import { FutureEstimatedRenewalCostsView, FutureEstimatedRenewalCostsViewMode } from "./Dashboard.types";
import { headerActionHeight } from "./DashboardWidget.styles";

export type FutureEstimatedRenewalCostsViewSelectorProps = {
    view: FutureEstimatedRenewalCostsView;
    availableYears: number[];
    onChange: (view: FutureEstimatedRenewalCostsView) => void;
};

const navy = "#000000";
const borderColor = "#707070";

const yearOption = (year: number): AutocompleteOption<number> => ({ label: year.toString(), value: year });

function FutureEstimatedRenewalCostsViewSelector(props: FutureEstimatedRenewalCostsViewSelectorProps) {
    // ToggleButtonGroup reports null when the already selected button is clicked again - keep the current mode.
    const onModeChange = (mode: FutureEstimatedRenewalCostsViewMode | null) => {
        if (!mode) return;
        props.onChange({ ...props.view, mode });
    };

    // DotLegalAutocomplete reports the selection as an array for the multi select case - a chart always has a year.
    const onYearChange = (selected: AutocompleteOption<number>[]) => {
        const year = selected[0]?.value;
        if (!year) return;
        props.onChange({ ...props.view, selectedYear: year });
    };

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ToggleButtonGroup
                exclusive
                size="small"
                value={props.view.mode}
                onChange={(_, mode) => onModeChange(mode as FutureEstimatedRenewalCostsViewMode | null)}
                sx={{
                    backgroundColor: "#FFFFFF",
                    height: headerActionHeight,
                    "& .MuiToggleButton-root": {
                        textTransform: "none",
                        fontSize: 12,
                        paddingY: 0,
                        paddingX: 1.5,
                        color: navy,
                        borderColor: borderColor,
                        "&.Mui-selected": {
                            backgroundColor: navy,
                            color: "#FFFFFF",
                            "&:hover": { backgroundColor: navy },
                        },
                    },
                    // The divider between the buttons is the first button's right border, and the second button is
                    // pulled 1px left on top of it with a transparent left border. Colouring that left border keeps
                    // the divider visible when the second button is selected and its fill would otherwise cover it.
                    "& .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
                        borderLeftColor: borderColor,
                    },
                }}
            >
                <ToggleButton value="year">Per year</ToggleButton>
                <ToggleButton value="month">Per month</ToggleButton>
            </ToggleButtonGroup>
            {props.view.mode === "month" && (
                // The same dropdown as the organisation settings dialog, wrapper id included - it is what makes a
                // single select fill its input (see the #singleSelectDropdown rules in App.styles.ts).
                <Box id="singleSelectDropdown" sx={{ width: 110 }}>
                    <DotLegalAutocomplete
                        disableClearable
                        onChange={onYearChange}
                        options={props.availableYears.map(yearOption)}
                        singleSelect
                        // DotLegalAutocomplete types value as an array for the multi select case, but a single
                        // select hands MUI the option itself - the same call shape the settings dialog uses.
                        // @ts-ignore
                        value={yearOption(props.view.selectedYear)}
                        width="100%"
                    />
                </Box>
            )}
        </Box>
    );
}

export default FutureEstimatedRenewalCostsViewSelector;
